import { Component, Inject, OnDestroy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { Jobs } from 'server/shared/collections/jobs';
import { getOpenJobsForTransferMongoQuery } from 'server/shared/helpers/jobs-query/getOpenJobsForTransferMongoQuery';
import { Department } from 'server/shared/models/department';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { Shift } from 'server/shared/models/shift';
import { PoolerService } from 'src/app/services/pooler/pooler.service';
import { defaultDispoJobColumns } from 'src/helpers/job/jobColumns';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { shouldWarnPoolerNotOnTargetDepartmentForTransfer } from 'src/helpers/shouldWarnPoolerNotOnTargetDepartmentForTransfer';
import { JobEditService } from '../../../../services/job-edit.service';
import { TranslationService } from '../../../../services/translation.service';
import { UserService } from '../../../../services/user.service';
import { ConfirmationDialog } from '../../../../shared/confirmation-dialog/confirmation.component';
import { SubscriptionService } from '../../../../shared/subscription.service';
import { EditTimeDialog } from '../edit-time-dialog/edit-time-dialog.component';

const transferDepartmentNotAssignedDialogId = 'transferDepartmentNotAssigned';

export type TransferDestinationColumnKey =
  | 'shift'
  | 'certificate'
  | 'description'
  | 'caseNumberShort';

@Component({
  selector: 'app-transfer-invitee-dialog',
  templateUrl: './transfer-invitee-dialog.component.html',
  styleUrls: ['./transfer-invitee-dialog.component.scss'],
})
export class TransferInviteeDialog implements OnDestroy {
  readonly destinationColumnWidths: Record<
    TransferDestinationColumnKey,
    string
  > = (
    ['shift', 'certificate', 'description', 'caseNumberShort'] as const
  ).reduce((acc, key) => {
    const col = defaultDispoJobColumns.find((c) => c.key === key);
    acc[key] = col?.width ?? '150px';
    return acc;
  }, {} as Record<TransferDestinationColumnKey, string>);

  shifts: Shift[];
  department$ = new BehaviorSubject<Department | undefined>(undefined);
  selectedJobType$ = new BehaviorSubject<JobTypeEnum>(this.data.job.jobType);
  transferReason: string = '';
  informInviteeOfReason: boolean = false;
  processing = false;

  i18n: Record<string, string | undefined> = {};

  loadingOpenJobs = false;
  transferDestinationId: string = 'new';

  private destroyed$ = new Subject<void>();

  invitee$ = new BehaviorSubject<Invitee | undefined>(undefined);

  inviteeUser$ = this.invitee$.pipe(
    startWith(undefined),
    map((invitee) => invitee?.userId),
    switchMap((userId) => {
      if (!userId) return of(undefined);
      return this.poolerService.poolerUsersById$.pipe(
        map((poolerUsersById) => poolerUsersById.get(userId)),
        rxDistinctJSONStringify(),
      );
    }),
    shareReplay(1),
  );

  openJobsForTransfer$ = combineLatest([
    of(this.data.job),
    this.inviteeUser$,
    this.department$,
    this.selectedJobType$,
    this.userService.readyDispo$,
  ]).pipe(
    switchMap(([job, inviteeUser, department, jobType, dispoUser]) => {
      if (!department || !inviteeUser || !dispoUser) return of([]);
      const query = getOpenJobsForTransferMongoQuery(
        job,
        department.id,
        jobType,
        inviteeUser,
      );
      return Jobs.find(query);
    }),
    takeUntil(this.destroyed$),
  );

  poolerDoesNotHaveJobType$ = combineLatest([
    this.inviteeUser$,
    this.selectedJobType$,
  ]).pipe(
    map(([user, selectedJobType]) => {
      return !!user?.jobTypes && !user.jobTypes.includes(selectedJobType);
    }),
    shareReplay(1),
  );

  constructor(
    public dialogRef: MatDialogRef<TransferInviteeDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { job: Job; inviteeUserId: string },
    private jobService: JobEditService,
    private dialog: MatDialog,
    private poolerService: PoolerService,
    private subscriptionService: SubscriptionService,
    private userService: UserService,
    private translationService: TranslationService,
  ) {
    this.i18n = this.translationService.getTranslations('jobs');
    this.shifts = JSON.parse(JSON.stringify(this.data.job.shifts));
    this.selectedJobType$.next(this.data.job.jobType);
    let invitee = data.job.invitees.find(
      (invitee) => invitee.userId === this.data.inviteeUserId,
    );
    if (invitee) {
      this.invitee$.next(invitee);
    }
    this.loadingOpenJobs = true;
    this.subscriptionService.jobs.subscribe();
    this.subscriptionService.jobs.ready$
      .pipe(
        filter((ready) => ready),
        take(1),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.loadingOpenJobs = false;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onJobTypeChange(jobType: JobTypeEnum) {
    this.selectedJobType$.next(jobType);
    if (this.department$.value) {
      this.selectDepartment(this.department$.value);
    }
  }

  selectDepartment(department?: Department) {
    if (!department) return;
    this.department$.next(department);
    const originalJobType = this.selectedJobType$.value;

    // For each original shift, find a matching shift in the department with fallbacks
    this.shifts = this.shifts
      .map((originalShift) => {
        // prefer a shift with the same jobtype requirement of the job
        const shiftMatchingOriginalShift = department.shifts.find(
          (shift) =>
            shift.shift === originalShift.shift &&
            shift.specificJobTypes?.includes(originalJobType) &&
            shift.specificJobTypes?.some((jobType) =>
              originalShift.specificJobTypes?.includes(jobType),
            ),
        );
        if (shiftMatchingOriginalShift) {
          return shiftMatchingOriginalShift;
        }
        // accept a job that fullfills the requirement (e.g. a shift with no specific job types is allowed for any job type)
        const shifftFullfillingRequirements = department.shifts.find(
          (shift) =>
            shift.shift === originalShift.shift &&
            this.shiftMatchesJobType(shift, originalJobType),
        );
        if (shifftFullfillingRequirements) {
          return shifftFullfillingRequirements;
        }

        // pick a similiar shift
        const shiftMatchingShiftType = department.shifts.find(
          (shift) => shift.shift === originalShift.shift,
        );
        if (shiftMatchingShiftType) {
          return shiftMatchingShiftType;
        }

        // just the first shift
        return department.shifts[0] || null;
      })
      .filter((shift): shift is Shift => shift !== null)
      .sort((a, b) => {
        if (!a.label && b.label) return -1;
        if (a.label && !b.label) return 1;
        return 0;
      });
  }

  /**
   * Check if a shift matches the given job type.
   * A shift matches if it has no specificJobTypes or if it includes the job type.
   */
  private shiftMatchesJobType(shift: Shift, jobType: JobTypeEnum): boolean {
    return (
      !shift.specificJobTypes ||
      shift.specificJobTypes.length === 0 ||
      shift.specificJobTypes.includes(jobType)
    );
  }

  openShiftEditDialog(event: Event, shift: Shift): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(EditTimeDialog, {
      data: shift,
    });
    dialogRef
      .afterClosed()
      .subscribe((result: { from: number; to: number }) => {
        if (result && (shift.from != result.from || shift.to != result.to)) {
          const newShift = this.shifts.find((s) => s.id === shift.id);
          if (!newShift) return;
          newShift.from = result.from;
          newShift.to = result.to;
        }
      });
  }

  transferInvitee() {
    if (!this.department$.value || this.processing) return;
    const newJobDepartment = {
      id: this.department$.value.id,
      name: this.department$.value.name,
    };

    combineLatest([this.inviteeUser$, this.invitee$])
      .pipe(take(1))
      .subscribe(([pooler, invitee]) => {
        void (async () => {
          if (!this.department$.value) {
            return;
          }
          const shouldWarn = shouldWarnPoolerNotOnTargetDepartmentForTransfer(
            pooler,
            this.department$.value,
            invitee,
          );
          if (shouldWarn) {
            const ref = this.dialog.open(ConfirmationDialog, {
              data: {
                id: transferDepartmentNotAssignedDialogId,
                title: this.i18n.transferInviteeDepartmentNotAssignedTitle,
                text: this.i18n.transferInviteeDepartmentNotAssignedWarning,
                confirmButtonLabel: this.i18n.transferInviteeTransferAnyway,
              },
            });
            const result = await firstValueFrom(ref.afterClosed());
            if (result !== transferDepartmentNotAssignedDialogId) {
              return;
            }
          }
          this.processing = true;
          const transferDepartmentNotAssignedConfirmed = shouldWarn;

          const transferPromise =
            this.transferDestinationId === 'new'
              ? this.jobService.transferInvitee(
                  this.data.job,
                  newJobDepartment,
                  this.shifts,
                  this.data.inviteeUserId,
                  this.transferReason,
                  this.informInviteeOfReason,
                  this.selectedJobType$.value,
                  transferDepartmentNotAssignedConfirmed,
                )
              : this.jobService.transferInviteeToExistingJob(
                  this.data.job,
                  newJobDepartment,
                  this.shifts,
                  this.data.inviteeUserId,
                  this.transferDestinationId,
                  this.transferReason,
                  this.informInviteeOfReason,
                  this.selectedJobType$.value,
                  transferDepartmentNotAssignedConfirmed,
                );

          transferPromise
            .then((jobId: string | Job) => {
              if (jobId) {
                this.close();
              }
              this.processing = false;
            })
            .catch(() => {
              this.processing = false;
            });
        })();
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  certificatesLabel(job: Job): string {
    if (!job.certificates?.length) return '';
    return job.certificates.map((c) => c.displayName).join(', ');
  }

  destinationColumnStyle(
    key: TransferDestinationColumnKey,
  ): Record<string, string> {
    const w = this.destinationColumnWidths[key];
    return {
      flex: `0 0 ${w}`,
      width: w,
      minWidth: w,
      maxWidth: w,
    };
  }
}
